import React, { useState, useEffect } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import BackToTop from "../../components/backToTop/BackToTop";
import "./../privacyPolicy/PrivacyPolicy.css"; // reuse same styling

const RefundCancellation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const filters = document.querySelector(".filters-row");

    const handleScroll = () => {
      if (!filters) return;

      const offset = filters.getBoundingClientRect().top;

      if (offset <= 80) {
        filters.classList.add("stuck");
      } else {
        filters.classList.remove("stuck");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  const handleSectionClick = () => {
    setIsSidebarOpen(false);
  };

  return (
    <>
      <Header
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
      />

      {/* ===== HERO ===== */}
      <section className="legal-hero">
        <div className="legal-container">
          <h1>Refund & Cancellation Policy</h1>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section className="legal-content-section">
        <div className="legal-container legal-grid">
          {/* ===== MOBILE SIDEBAR TOGGLE ===== */}
          <button
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            {isSidebarOpen ? "Close Sections" : "View Sections"}
          </button>

          {/* ===== OVERLAY (for slide animation) ===== */}
          {isSidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* ===== SIDEBAR ===== */}
          <aside className={`legal-sidebar ${isSidebarOpen ? "open" : ""}`}>
            <a href="#general" onClick={handleSectionClick}>
              General Policy
            </a>
            <a href="#student-cancel" onClick={handleSectionClick}>
              Student Cancellations
            </a>
            <a href="#tutor-cancel" onClick={handleSectionClick}>
              Tutor Cancellations
            </a>
            <a href="#refund-conditions" onClick={handleSectionClick}>
              Refund Conditions
            </a>
            <a href="#refund-process" onClick={handleSectionClick}>
              Refund Process
            </a>
            <a href="#disputes" onClick={handleSectionClick}>
              Disputes
            </a>
            <a href="#subscription" onClick={handleSectionClick}>
              Subscriptions
            </a>
            <a href="#changes" onClick={handleSectionClick}>
              Policy Changes
            </a>
            <a href="#contact" onClick={handleSectionClick}>
              Contact Us
            </a>
          </aside>

          {/* ===== MAIN CONTENT ===== */}
          <div className="legal-content">
            <p className="last-updated">Last Updated: 28 February, 2026</p>

            <p>
              At <strong>TutorsLinks</strong>, we strive to ensure a smooth
              learning experience for students, tutors, and training centres.
              This Refund & Cancellation Policy explains when and how refunds or
              cancellations may be requested.
            </p>

            {/* 1 */}
            <section id="general">
              <h2>1. General Policy</h2>
              <p>
                TutorsLinks serves as a <strong>linking platform</strong>{" "}
                between students, tutors, and centres. All bookings, payments,
                and cancellations are governed by this Policy.
              </p>
              <p>By using TUTORSLINKS, you agree to these terms.</p>
            </section>

            {/* 2 */}
            <section id="student-cancel">
              <h2>2. Cancellations by Students</h2>
              <ul>
                <li>
                  Students may cancel a booked class or session within{" "}
                  <strong>24 hours before the start time </strong>
                  to receive a <strong>full refund</strong>.
                </li>
                <li>
                  Cancellations <strong>less than 24 hours</strong> before the
                  session may result in
                  <strong> partial or no refund</strong> (depending on tutor
                  policy).
                </li>
                <li>
                  Missed classes without notice are{" "}
                  <strong>non-refundable</strong>.
                </li>
              </ul>
            </section>

            {/* 3 */}
            <section id="tutor-cancel">
              <h2>3. Cancellations by Tutors or Centres</h2>
              <ul>
                <li>
                  If a tutor or training centre cancels a confirmed class,
                  students are entitled to:
                  <ul>
                    <li>
                      A <strong>full refund</strong>, or
                    </li>
                    <li>
                      A <strong>rescheduled session</strong>, based on student
                      preference
                    </li>
                  </ul>
                </li>
                <li>
                  Repeated cancellations by tutors may lead to account review or
                  suspension.
                </li>
              </ul>
            </section>

            {/* 4 */}
            <section id="refund-conditions">
              <h2>4. Refund Conditions</h2>
              <p>Refunds will be processed if: </p>
              <ul>
                <li>
                  A tutor or centre fails to deliver a scheduled class or
                  course..
                </li>
                <li>
                  The service delivered is significantly different from what was
                  advertised.
                </li>
                <li>A payment was made in error (e.g., duplicate charge).</li>
              </ul>

              <p>
                Refunds are <strong>not applicable</strong> when:
              </p>
              <ul>
                <li>The student attended the class or used the service.</li>
                <li>The student attended the class or used the service. </li>
                <li>The student failed to show up without valid notice. </li>
                <li>
                  Course access has already been granted for digital/on-demand
                  programs.
                </li>
              </ul>
            </section>

            {/* 5 */}
            <section id="refund-process">
              <h2>5. Refund Process</h2>
              <ul>
                <li>
                  Refund requests must be submitted via{" "}
                  <strong>support@tutorslinks.com</strong> within{" "}
                  <strong>3 business days</strong> of the incident.
                </li>
                <li>
                  The TutorsLinks team will investigate and respond within{" "}
                  <strong>5–7 working days</strong>.
                </li>
                <li>
                  Approved refunds will be issued to the original payment method
                  within <strong>7–10 business days</strong>.
                </li>
                <li>
                  Any applicable transaction charges from payment processors may
                  be deducted.
                </li>
              </ul>
            </section>

            {/* 6 */}
            <section id="disputes">
              <h2>6. Disputes</h2>
              <p>
                If a disagreement arises between a student and a tutor regarding
                a refund:
              </p>
              <ul>
                <li>
                  Both parties are encouraged to resolve it amicably through the
                  chat system.
                </li>
                <li>
                  If unresolved, TutorsLinks’ support team will mediate based on
                  communication and evidence provided.
                </li>
                <li>
                  TutorsLinks’ decision after investigation shall be final.
                </li>
              </ul>
            </section>

            {/* 7 */}
            <section id="subscription">
              <h2>7. Subscription & Premium Services</h2>
              <p>For recurring or subscription-based services: </p>
              <ul>
                <li>Users can cancel anytime through their account settings. </li>
                <li>No refund will be issued for the current billing cycle, but future payments will 
stop immediately.</li>
              </ul>
            </section>

            {/* 8 */}
            <section id="changes">
              <h2>8. Changes to This Policy</h2>
              <p>
                TutorsLinks may update this Policy periodically. <br /> 
                All updates will be posted on the platform with the latest revision date.
              </p>
            </section>

            {/* 9 */}
            <section id="contact">
              <h2>9. Contact Us</h2>
              <p>For refund or cancellation requests, contact:</p>
              <p>
                <strong>TutorsLinks (Efiko Edutech Ltd.)</strong>
                <br />
                Email: <strong>support@tutorslinks.com</strong>
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </>
  );
};

export default RefundCancellation;
